import {
  CarrierType,
  CarrierVisitDirection,
  OrderResponseDto,
  OrderStatus,
} from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { GeneralCargoOrderLocationItem } from '@planning/pages/GeneralCargo/Components/GeneralCargoOrderLocationItem'
import { CreateGeneralCargoOrder } from '@planning/pages/Order/CreateGeneralCargoOrders'
import { IVesselVisitItem } from '@planning/pages/Order/stores/ContainerVisitItem'
import { GeneralCargoOrderListTableColumnsDto } from '@planning/pages/VesselVisit/Stores/VesselVisitOrderListViewStore'
import { getGeneralCargoListColumns } from '@planning/pages/VesselVisit/tableConfigs/generalCargoListColumns'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import { IRailVisitItem } from '@planning/rt-stores/railVisit/RailVisitItem'
import { useTranslate } from '@tolgee/react'
import { OptionsButton, TomTable, useDefaultMRTOptions, useTomTable } from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { FC, useMemo } from 'react'

type Props = {
  orders: IOrderItem[]
  carrierVisit: IVesselVisitItem | IRailVisitItem
  carrierVisitType: CarrierType
  direction: CarrierVisitDirection
  onOpenGeneralCargoReservation?: (order: OrderResponseDto) => void
  orderLocationsById?: Record<number, GeneralCargoOrderLocationItem[]>
  orderReservationsById?: Record<number, GeneralCargoOrderLocationItem[]>
}

export const VisitGeneralCargoTable: FC<Props> = observer(
  ({
    orders,
    carrierVisit,
    carrierVisitType,
    direction,
    onOpenGeneralCargoReservation,
    orderLocationsById,
    orderReservationsById,
  }) => {
    const { t } = useTranslate()

    const { generalCargoViewStore, dialogStore } = usePlanningStore()

    const handleEditOrder = async (order: OrderResponseDto) => {
      await generalCargoViewStore.getPackages()
      generalCargoViewStore.selectOrder(order)

      dialogStore.openDialog(
        <CreateGeneralCargoOrder
          disableCarrierVisitSelection
          forcedDirection={direction}
          carrierVisit={carrierVisit.data}
          carrierVisitType={carrierVisitType}
        />,
        true,
        {
          open: true,
          maxWidth: 'lg',
        },
      )
    }

    const getRowActions = (orderData: OrderResponseDto) => {
      const options = [
        {
          label: t('viewDetails', 'View details'),
          onClick: () => handleEditOrder(orderData),
        },
      ]

      if (onOpenGeneralCargoReservation && orderData.status === OrderStatus.Open) {
        options.push({
          label: t('reservation', 'Reservation'),
          onClick: async () => onOpenGeneralCargoReservation(orderData),
        })
      }

      return options
    }

    const defaultOptions = useDefaultMRTOptions<GeneralCargoOrderListTableColumnsDto>()

    const columns = useMemo(
      () =>
        getGeneralCargoListColumns(
          t,
          direction,
          carrierVisitType,
          orderLocationsById,
          orderReservationsById,
        ),
      [t, direction, carrierVisitType, orderLocationsById, orderReservationsById],
    )

    const mapGeneralCargoToTableData = (
      order: IOrderItem,
    ): GeneralCargoOrderListTableColumnsDto => {
      return {
        id: order.id,
        commodity: order.data.commodityName,
        referenceNumber: order.data.referenceNumber,
        lotNumber: order.data.lotNumber,
        status: order.data.status,
        customer: order.data.customerName,
        package: order.data.packageName,
        plannedQuantity: order.data.plannedCargoAmount,
        actualQuantity: order.data.actualCargoAmount,
        weight: order.data.grossWeight,
        weightUnit: order.data.weightUnit,
        imoClass:
          Array.isArray(order.data.imoClasses) && order.data.imoClasses.length > 0
            ? order.data.imoClasses.join(', ')
            : 'No',
        railTrackName: order.railcarTrackPosition?.data.railTrackName,
        railcarName: order.railcarTrackPosition?.data.railcarName,
      }
    }

    const mapppedGeneralCargoToTableDatas = useMemo(() => {
      return orders.map(order => mapGeneralCargoToTableData(order))
    }, [orders, orderLocationsById, orderReservationsById])

    const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
      'planning/visit-general-cargo',
    )

    const table = useTomTable<GeneralCargoOrderListTableColumnsDto>(
      {
        columns,
        data: mapppedGeneralCargoToTableDatas,
        getRowId: row => row.id.toString(),
        enableTopToolbar: false,
        enableRowSelection: false,
        enableExpanding: false,

        renderRowActions: ({ row }) => {
          const order = orders.find(o => o.id === row.original.id)

          const actions = order ? getRowActions(order.data) : []

          return !!actions?.length && <OptionsButton options={actions} position='right' />
        },
        initialState: {
          ...defaultOptions.initialState,
          columnPinning: {
            left: [
              ...(defaultOptions.initialState?.columnPinning?.left ?? []),
              'commodity',
              'referenceNumber',
              'lotNumber',
            ],
          },
        },
        muiTableContainerProps: {
          sx: {
            height: 'calc(100vh - 210px)',
          },
        },
      },
      undefined,
      undefined,
      { columns, savedPreferences, onPreferencesChange },
    )

    return <TomTable table={table} />
  },
)
